// src/hooks/useDoctorPatientClinicData.js
//
// The clinic data behind the doctor's patient record: vitals, this doctor's
// visits with the patient, and their prescriptions. Each loads on its own and
// carries its own status, so one slow or failing call never blocks or breaks
// another section (or the page). A 403 is a normal "not allowed" result here:
// these services pass treat403AsForbidden, so it never logs the doctor out.
//
// Nothing is fetched until `enabled` (the page passes true once the patient
// detail has loaded, so first paint is not slowed).

import { useCallback, useEffect, useRef, useState } from 'react';
import { getPatientVitals, listPatientVisits } from '../services/visitApi';
import { listPatientPrescriptions } from '../services/prescriptionApi';
import { LOAD_TIMEOUT_MS } from '../services/doctorApi';

export const VITALS_LIMIT = 50;

const LOADERS = {
  vitals: patientId => getPatientVitals(patientId, VITALS_LIMIT),
  visits: patientId => listPatientVisits(patientId),
  prescriptions: patientId => listPatientPrescriptions(patientId, { timeout: LOAD_TIMEOUT_MS }),
};
const SECTIONS = Object.keys(LOADERS);

const idle = () => ({ status: 'idle', data: [], error: null });
const initial = () => Object.fromEntries(SECTIONS.map(s => [s, idle()]));

/** Short, doctor-readable reason for a failed section. */
function describeError(err) {
  const code = err?.response?.status;
  if (code === 403) return 'You do not have access to this patient’s records here.';
  if (code >= 500) return 'The server had a problem loading this.';
  if (err?.code === 'ECONNABORTED') return 'This took too long to load.';
  return 'Could not load this.';
}

/**
 * @returns {{vitals, visits, prescriptions}} each {status: 'idle'|'loading'|'ready'|'error', data: [], error: string|null}
 *          plus reload(section | undefined)
 */
export default function useDoctorPatientClinicData(patientId, doctorEmail, enabled = true) {
  const [state, setState] = useState(initial);
  // Bumped per patient so a late response for the previous patient is dropped.
  const generation = useRef(0);

  const load = useCallback((section) => {
    const gen = generation.current;
    setState(prev => ({ ...prev, [section]: { ...prev[section], status: 'loading', error: null } }));
    return LOADERS[section](patientId, doctorEmail)
      .then(data => {
        if (gen !== generation.current) return;
        setState(prev => ({ ...prev, [section]: { status: 'ready', data: Array.isArray(data) ? data : [], error: null } }));
      })
      .catch(err => {
        if (gen !== generation.current) return;
        setState(prev => ({ ...prev, [section]: { status: 'error', data: prev[section].data, error: describeError(err) } }));
      });
  }, [patientId, doctorEmail]);

  useEffect(() => {
    generation.current += 1;
    setState(initial());
    if (!enabled || !patientId) return undefined;
    // Independent calls: each settles into its own slot (allSettled semantics).
    Promise.allSettled(SECTIONS.map(load));
    return () => { generation.current += 1; };
  }, [patientId, enabled, load]);

  const reload = useCallback(section => {
    if (section) return load(section);
    return Promise.allSettled(SECTIONS.map(load));
  }, [load]);

  return { ...state, reload };
}
